import { v4 as uuidv4 } from 'uuid';

// Type definitions
interface NewsArticle {
  article_id?: string;
  title: string;
  description: string;
  content?: string;
  pubDate: string;
  source_name?: string;
  link: string;
}

interface NewsApiOptions {
  countryCode?: string;
  city?: string;
  region?: string;
  maxResults?: number;
}

interface GeoThreat {
  id: string;
  type: string;
  location: string;
  coordinates: [number, number];
  status: 'active' | 'monitoring' | 'resolved';
  severity: 'low' | 'medium' | 'high' | 'critical';
  impactedCount: number;
  timestamp: Date;
  description: string;
  category: string;
  source: string;
  url: string;
}

// Mock data for development
const MOCK_ARTICLES: NewsArticle[] = [
  {
    article_id: 'mock-1',
    title: 'Major Earthquake Strikes Northern California',
    description: 'A 6.8 magnitude earthquake hit Northern California early this morning, affecting thousands of residents.',
    content: 'Emergency services are responding to a major earthquake that struck Northern California at 3:42 AM local time. The quake, measuring 6.8 on the Richter scale, has caused significant damage to infrastructure and forced evacuations in several communities.',
    pubDate: new Date().toISOString(),
    source_name: 'Emergency News Network',
    link: 'https://example.com/earthquake-california'
  },
  {
    article_id: 'mock-2',
    title: 'Wildfire Spreads Across 15,000 Acres in Colorado',
    description: 'Fast-moving wildfire threatens residential areas near Boulder, thousands evacuated.',
    content: 'A rapidly spreading wildfire has consumed over 15,000 acres of forest land near Boulder, Colorado. Authorities have issued mandatory evacuation orders for approximately 8,000 residents in the affected areas.',
    pubDate: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    source_name: 'Mountain News',
    link: 'https://example.com/wildfire-colorado'
  }
];

const LOCATION_MOCK_DATA: Record<string, NewsArticle[]> = {
  'us': MOCK_ARTICLES,
  'in': [
    {
      article_id: 'mock-india-1',
      title: 'Cyclone Threatens Gujarat Coast',
      description: 'Severe cyclonic storm approaches Gujarat, affecting millions.',
      content: 'A severe cyclonic storm is approaching the Gujarat coast with wind speeds of up to 120 km/h. Authorities have evacuated over 200,000 people from coastal areas.',
      pubDate: new Date().toISOString(),
      source_name: 'India Weather Service',
      link: 'https://example.com/cyclone-gujarat'
    },
    {
      article_id: 'mock-india-2',
      title: 'Chennai Floods Displace Thousands',
      description: 'Heavy monsoon rains cause severe flooding in Chennai metropolitan area.',
      content: 'Torrential rains have caused widespread flooding across Chennai, forcing thousands to evacuate their homes. Emergency services are working around the clock to rescue stranded residents.',
      pubDate: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      source_name: 'Chennai News Network',
      link: 'https://example.com/chennai-floods'
    }
  ]
};

export class NewsDataIODisasterExtractor {
  private readonly BASE_URL = 'https://newsdata.io/api/1';
  private readonly API_KEY: string;
  private readonly USE_MOCK_DATA: boolean;
  
  // Disaster type mappings with emojis
  private readonly DISASTER_CATEGORIES = {
    natural_disaster: {
      keywords: ['earthquake', 'tsunami', 'hurricane', 'tornado', 'flood', 'wildfire', 'volcano', 'landslide', 'avalanche', 'drought', 'cyclone'],
      emoji: '🌍'
    },
    human_health: {
      keywords: ['pandemic', 'epidemic', 'outbreak', 'disease', 'virus', 'infection', 'health crisis', 'medical emergency'],
      emoji: '❤️'
    },
    war_conflict: {
      keywords: ['war', 'conflict', 'battle', 'military', 'attack', 'bombing', 'shooting', 'terrorism', 'violence'],
      emoji: '⚔️'
    },
    animal_health: {
      keywords: ['animal disease', 'livestock', 'bird flu', 'avian flu', 'veterinary', 'animal health', 'wildlife disease'],
      emoji: '🐾'
    },
    plant_agriculture: {
      keywords: ['crop failure', 'harvest', 'agricultural', 'farming crisis', 'food shortage', 'plant disease'],
      emoji: '🌱'
    },
    environmental: {
      keywords: ['pollution', 'oil spill', 'toxic', 'contamination', 'environmental disaster', 'climate', 'chemical'],
      emoji: '🌍'
    },
    technological: {
      keywords: ['cyber attack', 'cyber', 'data breach', 'system failure', 'nuclear accident', 'infrastructure', 'blackout', 'explosion'],
      emoji: '💻'
    }
  };

  // Location to coordinates mapping for more realistic data
  private readonly LOCATION_COORDINATES: Record<string, [number, number]> = {
    'California': [-119.4179, 36.7783],
    'Northern California': [-122.4194, 37.7749],
    'San Francisco': [-122.4194, 37.7749],
    'Colorado': [-105.7821, 39.5501],
    'Boulder': [-105.2705, 40.0150],
    'Texas': [-99.9018, 31.9686],
    'Houston': [-95.3698, 29.7604],
    'Northeast': [-74.0060, 40.7128],
    'Louisiana': [-91.9623, 30.9843],
    'Oklahoma': [-97.5348, 35.5653],
    'Moorefield': [-97.7431, 35.2131],
    'Pacific Coast': [-122.4194, 37.7749],
    'Iowa': [-93.6091, 41.5868],
    'Nebraska': [-99.9018, 41.4925],
    'Kansas': [-98.4842, 38.5266],
    'Gujarat': [71.1924, 22.2587],
    'Kerala': [76.2711, 10.8505],
    'Chennai': [80.2707, 13.0827],
    'Mumbai': [72.8777, 19.0760],
    'Delhi': [77.1025, 28.7041],
    'London': [-0.1276, 51.5074],
    'Sydney': [151.2093, -33.8688]
  };

  // Enhanced disaster filtering keywords
  private readonly ACTIVE_DISASTER_VERBS = [
    'strikes', 'hits', 'devastates', 'destroys', 'erupts', 'explodes',
    'collapses', 'spreads', 'threatens', 'forces evacuation', 'evacuated',
    'emergency declared', 'state of emergency', 'disaster declared',
    'rescue operations', 'emergency response', 'first responders',
    'breaking', 'urgent', 'alert', 'warning issued', 'confirmed dead',
    'injured', 'missing', 'trapped', 'stranded', 'displaced'
  ];

  private readonly IMPACT_KEYWORDS = [
    'casualties', 'fatalities', 'deaths', 'injured', 'wounded', 'victims',
    'damage', 'destruction', 'devastation', 'crisis', 'emergency',
    'evacuation', 'rescue', 'relief', 'aid', 'assistance', 'shelter',
    'power outage', 'blackout', 'infrastructure failure', 'road closure',
    'airport closure', 'school closure', 'business closure', 'lockdown',
    'quarantine', 'state of emergency', 'disaster zone', 'affected area'
  ];

  private readonly NON_EVENT_KEYWORDS = [
    'research', 'study', 'analysis', 'report', 'forecast', 'prediction',
    'simulation', 'model', 'theory', 'history', 'anniversary', 'memorial',
    'documentary', 'movie', 'book', 'review', 'opinion', 'editorial',
    'advertisement', 'promotion', 'sale', 'discount', 'offer', 'deal',
    'conference', 'seminar', 'workshop', 'training', 'course', 'education',
    'investment', 'stock', 'market', 'economy', 'business', 'company',
    'technology', 'innovation', 'development', 'app', 'software', 'game'
  ];

  private readonly ADVERTISEMENT_INDICATORS = [
    'buy now', 'limited time', 'special offer', 'discount', 'sale',
    'free shipping', 'order today', 'call now', 'visit our website',
    'sponsored', 'advertisement', 'promo', 'deal of the day',
    'best price', 'lowest price', 'save money', 'exclusive offer'
  ];

  constructor(apiKey?: string) {
    // Use provided API key or get from environment variables
    this.API_KEY = apiKey || import.meta.env?.VITE_NEWSDATA_API_KEY || '';
    this.USE_MOCK_DATA = import.meta.env?.VITE_USE_MOCK_DATA === 'true' || !this.API_KEY;

    if (!this.API_KEY && !this.USE_MOCK_DATA) {
      console.warn('NewsData.io API key not found. Using mock data. Set VITE_NEWSDATA_API_KEY in your .env file for real data');
    }
  }

  // Enhanced method to check if article is about an actual disaster event
  private _isActualDisasterEvent(article: NewsArticle): boolean {
    const text = `${article.title} ${article.description} ${article.content || ''}`.toLowerCase();
    
    // Check for advertisement indicators first (immediate disqualification)
    const hasAdIndicators = this.ADVERTISEMENT_INDICATORS.some(indicator => 
      text.includes(indicator.toLowerCase())
    );
    if (hasAdIndicators) {
      return false;
    }

    // Check for non-event keywords (disqualifying factors)
    const hasNonEventKeywords = this.NON_EVENT_KEYWORDS.some(keyword => 
      text.includes(keyword.toLowerCase())
    );
    if (hasNonEventKeywords) {
      return false;
    }

    // Check for active disaster verbs (strong positive indicators)
    const hasActiveVerbs = this.ACTIVE_DISASTER_VERBS.some(verb => 
      text.includes(verb.toLowerCase())
    );

    // Check for impact keywords (positive indicators)
    const hasImpactKeywords = this.IMPACT_KEYWORDS.some(keyword => 
      text.includes(keyword.toLowerCase())
    );

    // Check for disaster category keywords
    const hasDisasterKeywords = Object.values(this.DISASTER_CATEGORIES).some(category =>
      category.keywords.some(keyword => text.includes(keyword.toLowerCase()))
    );

    // Additional checks for recency and urgency
    const hasUrgencyIndicators = [
      'breaking', 'urgent', 'immediate', 'now', 'currently', 'ongoing',
      'live', 'real-time', 'latest', 'update', 'developing'
    ].some(indicator => text.includes(indicator.toLowerCase()));

    // Check for specific time indicators that suggest current events
    const hasRecentTimeIndicators = [
      'today', 'yesterday', 'this morning', 'this evening', 'tonight',
      'hours ago', 'minutes ago', 'just reported', 'just confirmed'
    ].some(indicator => text.includes(indicator.toLowerCase()));

    // Scoring system for better filtering
    let score = 0;
    
    if (hasActiveVerbs) score += 3;
    if (hasImpactKeywords) score += 2;
    if (hasDisasterKeywords) score += 2;
    if (hasUrgencyIndicators) score += 1;
    if (hasRecentTimeIndicators) score += 1;

    // Check article age (prefer recent articles)
    const articleDate = new Date(article.pubDate);
    const now = new Date();
    const hoursSincePublication = (now.getTime() - articleDate.getTime()) / (1000 * 60 * 60);
    
    if (hoursSincePublication <= 24) score += 2; // Very recent
    else if (hoursSincePublication <= 72) score += 1; // Recent

    // Check for specific disaster magnitude indicators
    const hasMagnitudeIndicators = [
      'magnitude', 'category', 'level', 'scale', 'intensity',
      'mph', 'km/h', 'acres', 'hectares', 'people affected',
      'evacuated', 'displaced', 'injured', 'killed'
    ].some(indicator => text.includes(indicator.toLowerCase()));
    
    if (hasMagnitudeIndicators) score += 1;

    // Require minimum score for inclusion
    return score >= 4;
  }

  // FIXED: Generate simpler search queries that work with NewsData.io
  private generateSearchQueries(options?: NewsApiOptions): string[] {
    // Simple, single-keyword queries that work better with NewsData.io
    const baseQueries = [
      'earthquake',
      'hurricane', 
      'flood',
      'wildfire',
      'cyclone',
      'tornado',
      'disaster',
      'emergency'
    ];

    // Don't modify queries with location - use API's built-in location filtering instead
    return baseQueries;
  }

  // Fetch mock articles for development
  private async fetchMockArticles(options?: NewsApiOptions): Promise<NewsArticle[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return location-specific mock data if country code is provided
    if (options?.countryCode && LOCATION_MOCK_DATA[options.countryCode]) {
      let articles = LOCATION_MOCK_DATA[options.countryCode];
      
      // Filter by city if provided
      if (options.city) {
        articles = articles.filter(article => {
          const text = `${article.title} ${article.description} ${article.content || ''}`.toLowerCase();
          return text.includes(options.city!.toLowerCase());
        });
      }
      
      return articles;
    }
    
    return MOCK_ARTICLES;
  }

  // FIXED: Improved error handling and simpler API requests
  async fetchDisasterNews(options?: NewsApiOptions): Promise<NewsArticle[]> {
    if (this.USE_MOCK_DATA) {
      console.log('Using mock data for disaster news', options?.countryCode ? `(${options.countryCode})` : '(global)');
      return this.fetchMockArticles(options);
    }

    if (!this.API_KEY) {
      console.error('No API key provided for NewsData.io, falling back to mock data');
      return this.fetchMockArticles(options);
    }

    const queries = this.generateSearchQueries(options);
    const allArticles: NewsArticle[] = [];
    const maxResults = options?.maxResults || 10;

    // Try each query with better error handling
    for (const query of queries.slice(0, 3)) { // Limit to 3 queries to avoid rate limits
      try {
        // Build simple API URL
        let apiUrl = `${this.BASE_URL}/news?apikey=${this.API_KEY}&language=en&size=${Math.min(maxResults, 10)}`;
        
        // Add query parameter
        apiUrl += `&q=${encodeURIComponent(query)}`;
        
        // Add country filter if provided
        if (options?.countryCode) {
          apiUrl += `&country=${options.countryCode.toLowerCase()}`;
        }

        console.log(`Fetching from: ${apiUrl.replace(this.API_KEY, 'API_KEY_HIDDEN')}`);
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`NewsData.io request failed (${response.status}):`, errorText);
          
          if (response.status === 429) {
            console.warn('NewsData.io rate limit exceeded, falling back to mock data');
            return this.fetchMockArticles(options);
          }
          
          if (response.status === 422) {
            console.warn('NewsData.io query format error, trying next query or falling back to mock data');
            continue; // Try next query
          }
          
          throw new Error(`NewsData.io request failed: ${response.statusText}`);
        }

        const data = await response.json();
        
        if (data.status === 'success' && data.results && Array.isArray(data.results)) {
          console.log(`Found ${data.results.length} articles for query: ${query}`);
          allArticles.push(...data.results);
          
          // Break early if we have enough articles
          if (allArticles.length >= maxResults) {
            break;
          }
        } else {
          console.warn('Unexpected API response format:', data);
        }
        
        // Add delay between requests to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
        
      } catch (error) {
        console.error(`Error fetching news for query "${query}":`, error);
        // Continue with next query instead of failing completely
        continue;
      }
    }

    // If no articles found, fall back to mock data
    if (allArticles.length === 0) {
      console.log('No articles found from API, falling back to mock data');
      return this.fetchMockArticles(options);
    }

    // Remove duplicates based on title
    const uniqueArticles = allArticles.filter((article, index, self) => 
      index === self.findIndex(a => a.title === article.title)
    );

    // ENHANCED FILTERING: Apply disaster event filtering
    const filteredArticles = uniqueArticles.filter(article => 
      this._isActualDisasterEvent(article)
    );

    console.log(`Filtered ${uniqueArticles.length} articles down to ${filteredArticles.length} actual disaster events`);

    // Filter by location keywords if city/region is provided
    if (options?.city || options?.region) {
      const locationKeywords = [options.city, options.region].filter(Boolean);
      const locationFilteredArticles = filteredArticles.filter(article => {
        const text = `${article.title} ${article.description} ${article.content || ''}`.toLowerCase();
        return locationKeywords.some(keyword => 
          keyword && text.includes(keyword.toLowerCase())
        );
      });
      
      if (locationFilteredArticles.length > 0) {
        return locationFilteredArticles;
      }
    }

    return filteredArticles.length > 0 ? filteredArticles : this.fetchMockArticles(options);
  }

  // Extract disaster type using keyword matching
  private extractDisasterType(article: NewsArticle): { type: string; category: keyof typeof this.DISASTER_CATEGORIES } {
    const text = `${article.title} ${article.description} ${article.content || ''}`.toLowerCase();
    
    // Check each category for keyword matches
    for (const [category, config] of Object.entries(this.DISASTER_CATEGORIES)) {
      for (const keyword of config.keywords) {
        if (text.includes(keyword.toLowerCase())) {
          return {
            type: keyword,
            category: category as keyof typeof this.DISASTER_CATEGORIES
          };
        }
      }
    }

    return { type: 'disaster', category: 'natural_disaster' };
  }

  // Extract location using Named Entity Recognition (enhanced)
  private extractLocation(article: NewsArticle, userLocation?: { city?: string; region?: string; country?: string }): string {
    const text = `${article.title} ${article.description} ${article.content || ''}`;
    
    // Enhanced location patterns
    const locationPatterns = [
      // "in California", "in Northern California"
      /\bin\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*(?:\s+[A-Z][a-z]+)*)/g,
      // "California residents", "Texas communities"
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:residents|communities|area|region|coast)/g,
      // "near Boulder", "across Oklahoma"
      /(?:near|across|through)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g,
      // "Houston and surrounding areas"
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+and\s+surrounding/g,
      // State/city names followed by descriptors
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:farms|plant|facility|grid|infrastructure)/g
    ];

    for (const pattern of locationPatterns) {
      const matches = Array.from(text.matchAll(pattern));
      if (matches.length > 0) {
        const location = matches[0][1].trim();
        // Filter out common non-location words
        if (!['Emergency', 'National', 'Federal', 'State', 'Local', 'News', 'Alert'].includes(location)) {
          return location;
        }
      }
    }

    // Use user location as fallback if available
    if (userLocation) {
      return userLocation.city || userLocation.region || userLocation.country || 'Unknown Location';
    }

    return 'Unknown Location';
  }

  // Get coordinates for location
  private getLocationCoordinates(location: string): [number, number] {
    // Try exact match first
    if (this.LOCATION_COORDINATES[location]) {
      return this.LOCATION_COORDINATES[location];
    }

    // Try partial matches
    for (const [key, coords] of Object.entries(this.LOCATION_COORDINATES)) {
      if (location.includes(key) || key.includes(location)) {
        return coords;
      }
    }

    // Generate random coordinates as fallback
    const lat = (Math.random() - 0.5) * 160; // -80 to 80
    const lng = (Math.random() - 0.5) * 360; // -180 to 180
    return [lng, lat];
  }

  // Determine status based on recency
  private getStatus(pubDate: string): 'active' | 'monitoring' | 'resolved' {
    const publishedDate = new Date(pubDate);
    const now = new Date();
    const daysDiff = (now.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24);

    if (daysDiff <= 1) return 'active';
    if (daysDiff <= 7) return 'monitoring';
    return 'resolved';
  }

  // Extract impact count using enhanced pattern matching
  private extractImpactedCount(article: NewsArticle): number {
    const text = `${article.title} ${article.description} ${article.content || ''}`;
    
    // Enhanced patterns for number extraction
    const impactPatterns = [
      /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:million|mil)?\s+(?:people|residents|customers|individuals|victims|affected|injured|dead|casualties|evacuated|displaced)/gi,
      /(?:affects?|impacts?|threatens?)\s+(?:over\s+)?(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:million|mil)?/gi,
      /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:million|mil)?\s+(?:homes?|buildings?|families?|structures?)/gi,
      /(?:over|more than|up to)\s+(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:million|mil)?/gi,
      /(\d+(?:,\d{3})*(?:\.\d+)?)\s+(?:acres?|square miles?|hectares?)/gi
    ];

    let maxCount = 0;
    
    for (const pattern of impactPatterns) {
      const matches = Array.from(text.matchAll(pattern));
      for (const match of matches) {
        let count = parseFloat(match[1].replace(/,/g, ''));
        
        // Handle millions
        if (match[0].toLowerCase().includes('million') || match[0].toLowerCase().includes('mil')) {
          count *= 1000000;
        }
        
        if (count > maxCount) {
          maxCount = count;
        }
      }
    }

    return Math.floor(maxCount) || Math.floor(Math.random() * 50000) + 1000; // Fallback with random number
  }

  // Determine severity based on multiple factors
  private getSeverity(impactedCount: number, text: string): 'low' | 'medium' | 'high' | 'critical' {
    const lowerText = text.toLowerCase();
    
    // Critical keywords
    const criticalKeywords = /death|dead|kill|fatal|casualt|devastat|destroy|explosion|toxic|evacuat/i;
    const highKeywords = /major|severe|emergency|disaster|crisis|widespread/i;
    const mediumKeywords = /damage|affected|impact|concern|alert/i;
    
    if (criticalKeywords.test(lowerText) && impactedCount > 10000) return 'critical';
    if (criticalKeywords.test(lowerText) || impactedCount > 100000) return 'high';
    if (highKeywords.test(lowerText) || impactedCount > 10000) return 'medium';
    if (mediumKeywords.test(lowerText) || impactedCount > 1000) return 'low';
    
    return 'low';
  }

  // Generate unique ID based on article content
  private generateId(article: NewsArticle): string {
    return article.article_id || uuidv4();
  }

  // Main extraction method with location support
  async extractDisasters(options?: NewsApiOptions): Promise<GeoThreat[]> {
    const articles = await this.fetchDisasterNews(options);
    const disasters: GeoThreat[] = [];

    const userLocation = options ? {
      city: options.city,
      region: options.region,
      country: options.countryCode
    } : undefined;

    for (const article of articles) {
      try {
        const { type, category } = this.extractDisasterType(article);
        const location = this.extractLocation(article, userLocation);
        const coordinates = this.getLocationCoordinates(location);
        const status = this.getStatus(article.pubDate);
        const impactedCount = this.extractImpactedCount(article);
        const severity = this.getSeverity(impactedCount, `${article.title} ${article.description}`);
        const id = this.generateId(article);

        const disaster: GeoThreat = {
          id,
          type,
          location,
          coordinates,
          status,
          severity,
          impactedCount,
          timestamp: new Date(article.pubDate),
          description: article.description || article.title,
          category,
          source: article.source_name || 'News Source',
          url: article.link
        };

        disasters.push(disaster);
      } catch (error) {
        console.error('Error processing article:', article.title, error);
      }
    }

    return disasters.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  // Method to switch between mock and real data
  setUseMockData(useMock: boolean): void {
    (this as any).USE_MOCK_DATA = useMock;
  }

  // Method to check if using mock data
  isUsingMockData(): boolean {
    return this.USE_MOCK_DATA;
  }
}

// Create an instance with environment variable
export const disasterExtractor = new NewsDataIODisasterExtractor();